// A guide's version history on the Guides page: its versions, one read as it was, and a restore of that
// version as a new change. (A launched tour runs in the shell's Spotlight.)
import { GitCommitHorizontal, History, Loader2, RotateCcw, X } from 'lucide-react';
import * as React from 'react';
import { Markdown, type AssetRenderer } from '@cortex/ui';
import { useGuidesClient, useGuidesHost } from './host.tsx';
import type { Guide } from './model.ts';

const G = '/guides' as const;
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50';

function useEscape(onClose: () => void) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

type Commit = { hash: string; author: string; email: string; date: string; message: string };
type Version = { commit: string; title: string; description: string | null; sections: Guide['sectionList'] };

const when = (d: string) =>
  new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** A guide's versions; pick one to read the guide as it was, and restore it (edit access). */
export function GuideHistory({
  guide,
  canEdit,
  asset,
  onClose,
  onRestored,
}: {
  guide: { key: string; title: string };
  canEdit: boolean;
  asset: AssetRenderer;
  onClose: () => void;
  onRestored: (g: Guide, commit: string) => void;
}) {
  const api = useGuidesClient();
  const scope = useGuidesHost().scope;
  const [commits, setCommits] = React.useState<Commit[] | null>(null);
  const [picked, setPicked] = React.useState<string | null>(null);
  const [version, setVersion] = React.useState<Version | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  useEscape(onClose);
  const path = { key: guide.key };

  React.useEffect(() => {
    let alive = true;
    void api.GET(`${G}/{key}/history`, { params: { path } }).then(({ data, error }) => {
      if (!alive) return;
      if (data) setCommits(data.commits);
      else setErr((error as { message?: string } | undefined)?.message ?? 'The history could not be read');
    });
    return () => {
      alive = false;
    };
  }, [scope, guide.key]);

  React.useEffect(() => {
    setVersion(null);
    if (!picked) return;
    let alive = true;
    void api
      .GET(`${G}/{key}/history/{commit}`, { params: { path: { ...path, commit: picked } } })
      .then(({ data, error }) => {
        if (!alive) return;
        if (data) setVersion(data);
        else setErr((error as { message?: string } | undefined)?.message ?? 'That version could not be read');
      });
    return () => {
      alive = false;
    };
  }, [picked]);

  const restore = async () => {
    if (!picked) return;
    setBusy(true);
    setErr('');
    const { data, error } = await api.POST(`${G}/{key}/restore`, {
      params: { path },
      body: { commit: picked },
    });
    setBusy(false);
    if (data) onRestored(data, picked);
    else setErr((error as { message?: string } | undefined)?.message ?? 'The restore failed');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Version history — ${guide.title}`}
      data-testid="guide-history"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl rounded-lg bg-card-bg text-page-text shadow-xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-card-border">
          <History size={16} className="text-nav-active" />
          <div className="font-semibold flex-1 truncate">Version history — {guide.title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-1 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text"
          >
            <X size={16} />
          </button>
        </div>
        {err ? <div className="px-5 pt-3 text-sm text-red-600">{err}</div> : null}
        <div className="flex min-h-0 flex-1">
          <div className="w-72 shrink-0 border-r border-card-border overflow-y-auto p-2">
            {commits === null && !err ? (
              <div className="p-3 text-sm text-page-muted flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : null}
            {(commits ?? []).map((c, n) => (
              <button
                key={c.hash}
                type="button"
                data-testid={`guide-history-commit-${n}`}
                data-commit={c.hash}
                onClick={() => setPicked(c.hash)}
                className={
                  'w-full text-left rounded-md px-2.5 py-2 mb-1 border cursor-pointer bg-transparent text-inherit ' +
                  (picked === c.hash
                    ? 'border-nav-active bg-nav-active/5'
                    : 'border-transparent hover:bg-card-border/30')
                }
              >
                <div className="flex items-center gap-1.5 text-xs">
                  <GitCommitHorizontal size={12} className="text-page-muted" />
                  <code className="text-page-muted">{c.hash.slice(0, 8)}</code>
                  {n === 0 ? (
                    <span className="text-[10px] rounded-full px-1.5 bg-emerald-500/15 text-emerald-600">
                      current
                    </span>
                  ) : null}
                </div>
                <div className="text-sm truncate">{c.message.replace(/^guide [^:]+: /, '')}</div>
                <div className="text-[11px] text-page-muted truncate">
                  {c.email || c.author} · {when(c.date)}
                </div>
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-0 overflow-y-auto p-5" data-testid="guide-history-version">
            {!picked ? (
              <div className="text-sm text-page-muted">Pick a version to read the guide as it was.</div>
            ) : !version ? (
              <div className="text-sm text-page-muted flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-semibold" data-testid="guide-history-title">
                      {version.title}
                    </div>
                    {version.description ? (
                      <div className="text-sm text-page-muted">{version.description}</div>
                    ) : null}
                  </div>
                  {canEdit && picked !== commits?.[0]?.hash ? (
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={busy}
                      onClick={() => void restore()}
                      data-testid="guide-history-restore"
                    >
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}{' '}
                      Restore this version
                    </button>
                  ) : null}
                </div>
                {version.sections.map((s, i) => (
                  <section key={s.id} className="py-2 border-b border-card-border/60 last:border-b-0">
                    <div className="text-sm font-medium" data-testid={`guide-history-section-${i}`}>
                      {i + 1}. {s.heading}
                    </div>
                    <Markdown source={s.body} asset={asset} />
                  </section>
                ))}
                {!version.sections.length ? (
                  <div className="text-sm text-page-muted">No sections in this version.</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
